'use client'

import { useId, type ComponentProps, type ReactNode } from 'react'

import { useHasTouchInput } from '../../hooks/useHasTouchInput'
import Info from '../../icons/Info'
import {
  Toggletip,
  ToggletipContent,
  ToggletipTrigger,
  type ToggletipProps
} from '../_base/Toggletip'
import { Tooltip, TooltipContent, TooltipTrigger } from '../_base/Tooltip'

interface InfoTooltipProps extends Omit<ToggletipProps, 'children'> {
  content: string | React.ReactNode
  title?: string
  trigger?: ReactNode
  showInfoIcon?: boolean
  infoIconSize?: number
  tooltipContentProps?: ComponentProps<typeof ToggletipContent>
}

export const INFO_TOOLTIP_ICON_SIZE = 14
export const INFO_TOOLTIP_ICON_CLASS_NAME =
  'text-muted-foreground group-hover:text-foreground group-focus-visible:text-foreground transition-colors'

const iconTriggerClassName =
  'group focus-visible:ring-ring/50 flex w-fit cursor-help items-center gap-1 rounded-sm outline-none focus-visible:ring-[3px]'

/**
 * InfoTooltip displays an info icon with a toggletip (a tooltip that also opens on tap on touch
 * devices), without unwanted button styling.
 * Use to provide contextual information with a clean, minimal trigger.
 *
 * A custom `trigger` may already act on tap (sortable header, `<Label htmlFor>`), so it never
 * becomes a tap target: with the info icon, touch input opens the explanation from the icon only;
 * without it, the trigger stays a hover tooltip.
 */
export function InfoTooltip({
  content,
  title,
  trigger,
  showInfoIcon,
  infoIconSize = INFO_TOOLTIP_ICON_SIZE,
  tooltipContentProps,
  ...tooltipProps
}: InfoTooltipProps) {
  'use memo'

  const hasTouchInput = useHasTouchInput()
  const iconId = useId()
  const triggerLabelId = useId()
  const shouldShowInfoIcon = showInfoIcon ?? !trigger

  if (!trigger && !shouldShowInfoIcon) {
    return null
  }

  if (!shouldShowInfoIcon) {
    const { onOpenAutoFocus: _onOpenAutoFocus, ...contentProps } = tooltipContentProps ?? {}
    return (
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent title={title} {...contentProps}>
          {content}
        </TooltipContent>
      </Tooltip>
    )
  }

  const icon = (
    <Info width={infoIconSize} height={infoIconSize} className={INFO_TOOLTIP_ICON_CLASS_NAME} />
  )
  const iconOnlyTrigger = trigger && hasTouchInput

  const toggletipTrigger = (
    <span
      className={iconTriggerClassName}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.code === 'Space') {
          e.preventDefault()
          e.currentTarget.click()
        }
      }}
      id={iconId}
      aria-label={trigger && !iconOnlyTrigger ? undefined : 'More info'}
      // Names a split-off icon after its trigger ("More info Maturity") so each one is distinct.
      aria-labelledby={iconOnlyTrigger ? `${iconId} ${triggerLabelId}` : undefined}
    >
      {!iconOnlyTrigger && trigger}
      {icon}
    </span>
  )

  const toggletip = (
    <Toggletip {...tooltipProps}>
      <ToggletipTrigger asChild>{toggletipTrigger}</ToggletipTrigger>
      <ToggletipContent title={title} {...tooltipContentProps}>
        {content}
      </ToggletipContent>
    </Toggletip>
  )

  if (!iconOnlyTrigger) return toggletip

  return (
    <span className="flex w-fit items-center gap-1">
      <span id={triggerLabelId}>{trigger}</span>
      {toggletip}
    </span>
  )
}
